import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { doc, getDoc, updateDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { 
  Printer, ArrowLeft, Plus, Trash2, 
  Activity, ClipboardList, User, 
  Stethoscope, Pill, FileText, Calendar, CheckCircle2,
  Clock, Weight, Coffee, Utensils, PenTool, Syringe
} from "lucide-react";

const ConsultationPage = () => {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [hospitalInfo, setHospitalInfo] = useState(null);
  const [patient, setPatient] = useState(location.state?.patient || null);
  const [diagnosis, setDiagnosis] = useState("");
  const [complaints, setComplaints] = useState(""); 
  const [injection, setInjection] = useState("");
  const [weight, setWeight] = useState("");
  const [medicines, setMedicines] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
const [showSuggestions, setShowSuggestions] = useState(false);
  const [newMed, setNewMed] = useState({ 
  medicineName: "",
  syrupName: "",
  dosage: {
  morning:false,
  afternoon:false,
  evening:false,
  night:false
},
  timing: "After Food",
  duration: "5 Days",
  ml: ""
});
const medicineList = [
  "Paracetamol",
  "Azithromycin",
  "Amoxicillin",
  "Cetirizine",
  "Dolo 650",
  "Pantoprazole",
  "Ibuprofen",
  "Metformin",
  "Amlodipine",
  "ORS",
];
const filteredMedicines = medicineList.filter((med) =>
  med.toLowerCase().includes(newMed.medicineName.toLowerCase())
);

  useEffect(() => {
    const fetchHospitalData = async () => {
      const user = auth.currentUser;
      if (user) {
        const hSnap = await getDoc(doc(db, "hospitals", user.uid));
        if (hSnap.exists()) setHospitalInfo(hSnap.data());
      }
      if (!patient && user) {
        const pSnap = await getDoc(doc(db, "hospitals", user.uid, "patients", patientId));
        if (pSnap.exists()) setPatient({ id: pSnap.id, ...pSnap.data() });
      }
    };
    fetchHospitalData();
  }, [patientId, patient]);

 const isSyrup = newMed.syrupName.length > 0;

  const addMedicine = () => {

  if (!newMed.medicineName && !newMed.syrupName)
    return alert("Enter medicine or syrup name");

  const name = newMed.medicineName || newMed.syrupName;

  const medicineToSave = {
  id: Date.now(),
  name: name,
  dosage: newMed.dosage,
  ml: newMed.ml,
  timing: newMed.timing,
  duration: newMed.duration
};

  setMedicines([...medicines, medicineToSave]);

  setNewMed({
    medicineName: "",
    syrupName: "",
    dosage:{
 morning:false,
 afternoon:false,
 evening:false,
 night:false
},
    timing: "After Food",
    duration: "5 Days",
    ml: "",
    isInjection: false
  });

};

  const handleJustPrint = () => window.print();

  const handleCompleteConsultation = async () => {
    if (!diagnosis) return alert("Please enter clinical diagnosis.");
    if (!window.confirm("Complete consultation?")) return;
    
    setIsSubmitting(true);
    try {
      const user = auth.currentUser;
      await updateDoc(doc(db, "hospitals", user.uid, "patients", patient.id), {
        visitHistory: arrayUnion({ date: new Date().toISOString(), complaints, diagnosis, injection, weight, medicines }),
        lastVisit: serverTimestamp(),
        status: "consulted"
      });
      const hospitalSlug = hospitalInfo.hospitalName.replace(/\s+/g, '-').toLowerCase();
      navigate(`/${hospitalSlug}/dashboard`);
    } catch (err) { alert(err.message); }
    finally { setIsSubmitting(false); }
  };

  const inputStyle = "w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-semibold transition-all print:border-none print:p-0";
  const labelStyle = "flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.1em] mb-2 print:text-blue-600";

  if (!hospitalInfo || !patient) return <div className="p-20 text-center font-black text-slate-400 uppercase tracking-widest animate-pulse text-xs">Loading Clinical Data...</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 print:p-0 font-sans bg-slate-50 min-h-screen print:bg-white">
      
      {/* Header Buttons */}
      <div className="mb-6 flex justify-between items-center print:hidden">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 font-bold text-xs uppercase transition-all"><ArrowLeft size={16}/> Back</button>
        <div className="flex gap-3">
          <button onClick={handleJustPrint} className="px-6 py-3 bg-white border border-slate-200 rounded-xl font-black text-[10px] uppercase flex items-center gap-2 shadow-sm"><Printer size={16}/> Print</button>
          <button onClick={handleCompleteConsultation} disabled={isSubmitting} className="px-8 py-3 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase flex items-center gap-2 shadow-lg shadow-blue-200"><CheckCircle2 size={16}/> {isSubmitting ? "Saving..." : "Complete"}</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-xl shadow-slate-200/50 print:border-none print:shadow-none">
{/* Hospital Header */}
<div className="p-8 border-b border-slate-100 flex flex-col md:flex-row justify-between items-center gap-6 print:p-5">
  <div className="flex items-center gap-6">
    <div className="bg-slate-900 p-4 rounded-2xl text-white shadow-xl shadow-slate-200 print:text-blue-600 print:bg-transparent print:p-0 print:shadow-none">
      <Stethoscope size={32} />
    </div>
    <div className="text-center md:text-left">
      <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight print:text-2xl leading-none">
        {hospitalInfo.hospitalName}
      </h1>
      <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
          <Activity size={12} className="text-blue-500" /> {hospitalInfo.address}
        </p>
      </div>
    </div>
  </div>
  
  <div className="hidden md:block text-right border-l border-slate-100 pl-6 print:block">
    <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Consulting Doctor</p>
   
  </div>
</div>

{/* Patient Bar - Clean & Adjustable */}
{/* Patient Bar - Clean & Adjustable */}
<div className="mx-8 mt-8 mb-4 p-5 bg-white border border-slate-100 rounded-[2rem] shadow-sm flex flex-wrap md:flex-nowrap justify-between gap-4 print:mx-0 print:my-6 print:bg-transparent print:border-y-2 print:border-x-0 print:border-slate-200 print:rounded-none print:shadow-none print:px-2">
  <div className="space-y-1 min-w-[140px]">
    <div className="flex items-center gap-1.5">
      <User size={12} className="text-blue-500 print:text-slate-500" />
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Patient Name</p>
    </div>
    <p className="text-sm font-black text-slate-800 pl-4.5">{patient.name}</p>
  </div>

  <div className="space-y-1 min-w-[100px]">
    <div className="flex items-center gap-1.5">
      <Calendar size={12} className="text-blue-500 print:text-slate-500" />
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Age / Gender</p>
    </div>
    <p className="text-sm font-black text-slate-800 pl-4.5">{patient.age}Y / {patient.gender}</p>
  </div>

  <div className="space-y-1 min-w-[80px]">
    <div className="flex items-center gap-1.5">
      <FileText size={12} className="text-blue-500 print:text-slate-500" />
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Patient ID</p>
    </div>
    <p className="text-sm font-black text-blue-600 print:text-slate-800 pl-4.5">#{patient.patientId}</p>
  </div>

  <div className="space-y-1 md:text-right min-w-[100px]">
    <div className="flex items-center md:justify-end gap-1.5">
      <Clock size={12} className="text-blue-500 print:text-slate-500" />
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Visit Date</p>
    </div>
    <p className="text-sm font-black text-slate-800">{new Date().toLocaleDateString('en-GB')}</p>
  </div>
</div>

<div className="px-10 pb-10 grid grid-cols-12 gap-10 print:px-0 print:gap-6 print:mt-4">
  {/* Clinical Info */}
  <div className="col-span-12 lg:col-span-4 space-y-6 print:col-span-4">
    <section>
      <label className={labelStyle}><Weight size={14}/> Weight (kg)</label>
      <p className="hidden print:block text-sm font-bold border-b border-dashed border-slate-300 pb-1">{weight || "---"} kg</p>
      <input type="text" value={weight} onChange={(e)=>setWeight(e.target.value)} className={`${inputStyle} print:hidden`} placeholder="0.0" />
    </section>
    
    <section>
      <label className={labelStyle}><Syringe size={14}/> Injection</label>
      <p className="hidden print:block text-sm font-bold min-h-[40px] leading-relaxed">{injection || "None"}</p>
      <textarea className={`${inputStyle} h-24 resize-none print:hidden`} value={injection} onChange={(e)=>setInjection(e.target.value)} placeholder="Injection details..." />
    </section>

    <section>
      <label className={labelStyle}><ClipboardList size={14}/> Diagnosis</label>
      <p className="hidden print:block text-sm font-black text-blue-700 uppercase leading-relaxed">{diagnosis || "---"}</p>
      <textarea className={`${inputStyle} h-24 resize-none font-black text-blue-600 bg-blue-50/30 print:hidden`} value={diagnosis} onChange={(e)=>setDiagnosis(e.target.value)} />
    </section>
  </div>

          {/* Rx Section */}
          <div className="col-span-12 lg:col-span-8 print:col-span-8">
    <div className="flex items-center gap-2 mb-6 border-b-2 border-slate-900 pb-2 print:border-slate-800">
      <Pill size={18} className="text-blue-600 print:text-slate-800"/>
      <span className="text-xs font-black uppercase tracking-widest">Prescription (Rx)</span>
    </div>
            {/* Medicine Input (Hidden in Print) */}
            {/* Medicine Input (Hidden in Print) */}
{/* Medicine Input (Hidden in Print) */}
<div className="bg-slate-50 p-5 rounded-3xl border border-slate-100 mb-6 print:hidden shadow-inner">
  
  <div className="flex flex-col gap-4">
    
    {/* ROW 1: Medicine or Syrup Input + Dosage Buttons */}
    <div className="flex flex-col lg:flex-row gap-3">
      
      {/* LEFT SIDE: Tablet/Syrup Search & ML */}
      <div className="flex-1 flex flex-col sm:flex-row gap-3">
        
        {/* Medicine/Syrup Name Input Container */}
        <div className="relative flex-1 group">
          <div className="flex items-center bg-white rounded-xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all overflow-hidden">
            <input
              className="flex-1 p-3 text-xs font-bold outline-none bg-transparent min-w-[150px]"
              placeholder="Medicine / Syrup Name..."
              value={newMed.medicineName || newMed.syrupName}
              onChange={(e) => {
                const val = e.target.value;
                setNewMed({
                  ...newMed,
                  medicineName: val,
                  syrupName: val // Both updated to show in the same UI field
                });
                setShowSuggestions(true);
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            />
            
            {/* ML Box inside the input field - Only shows if typing */}
            {(newMed.medicineName || newMed.syrupName) && (
              <div className="flex items-center gap-1 pr-2">
                <span className="text-[10px] font-black text-slate-400">ML:</span>
                <input
                  type="text"
                  placeholder="0"
                  className="w-12 p-1.5 text-xs font-black bg-blue-50 text-blue-600 rounded-lg outline-none text-center border border-blue-100"
                  value={newMed.ml}
                  onChange={(e) => setNewMed({ ...newMed, ml: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* AUTO SUGGESTION DROPDOWN */}
          {showSuggestions && filteredMedicines.length > 0 && (
            <div className="absolute left-0 right-0 bg-white border border-slate-200 mt-1 rounded-xl shadow-2xl z-30 max-h-48 overflow-y-auto">
              {filteredMedicines.map((med, i) => (
                <div
                  key={i}
                  onClick={() => {
                    setNewMed({ ...newMed, medicineName: med, syrupName: med });
                    setShowSuggestions(false);
                  }}
                  className="p-3 text-xs hover:bg-blue-50 cursor-pointer font-bold border-b border-slate-50 last:border-none flex items-center justify-between"
                >
                  {med}
                  <Plus size={12} className="text-slate-300" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* DOSAGE BUTTONS (M-A-E-N) */}
        {(newMed.medicineName || newMed.syrupName) && (
          <div className="flex items-center justify-center gap-1 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm self-center">
            {["morning", "afternoon", "evening", "night"].map((time) => {
              const label = time === "morning" ? "M" : time === "afternoon" ? "A/N" : time === "evening" ? "E" : "N";
              return (
                <button
                  key={time}
                  onClick={() => setNewMed({
                    ...newMed,
                    dosage: { ...newMed.dosage, [time]: !newMed.dosage[time] }
                  })}
                  className={`w-9 h-8 rounded-lg flex items-center justify-center text-[10px] font-black transition-all ${
                    newMed.dosage[time] 
                    ? "bg-blue-600 text-white shadow-md shadow-blue-200" 
                    : "bg-slate-50 text-slate-400 hover:bg-slate-100"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>

    {/* ROW 2: Timing, Duration and Add Button */}
    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-4">
      
      {/* Before/After Food Toggle */}
      <div className="flex bg-white rounded-xl border border-slate-200 p-1 shadow-sm">
        {["After Food", "Before Food"].map(t => (
          <button
            key={t}
            onClick={() => setNewMed({ ...newMed, timing: t })}
            className={`px-4 py-2 text-[9px] font-black uppercase rounded-lg transition-all ${
              newMed.timing === t ? "bg-blue-600 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-1 sm:flex-none">
        {/* Duration Input */}
        <div className="flex items-center bg-white px-3 py-1 rounded-xl border border-slate-200 gap-2 flex-1">
          <Clock size={14} className="text-slate-400"/>
          <input
            className="w-full sm:w-20 p-2 text-xs font-bold outline-none"
            placeholder="Duration"
            value={newMed.duration}
            onChange={(e) => setNewMed({ ...newMed, duration: e.target.value })}
          />
        </div>

        {/* Add Button */}
        <button
          onClick={addMedicine}
          className="bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase flex items-center gap-2 hover:bg-blue-600 transition-all shadow-lg active:scale-95 whitespace-nowrap"
        >
          <Plus size={16} /> Add Rx
        </button>
      </div>
    </div>
  </div>
</div>

{/* Medicine Table */}
{/* Medicine Table */}
<table className="w-full border-collapse">
      <thead>
        <tr className="border-b-2 border-slate-200">
          <th className="py-3 text-left text-[9px] font-black text-slate-400 uppercase print:text-slate-600">Medicine</th>
          <th className="py-3 text-center text-[9px] font-black text-slate-400 uppercase print:text-slate-600">Dosage / ML</th>
          <th className="py-3 text-right text-[9px] font-black text-slate-400 uppercase print:text-slate-600">Timing</th>
          <th className="print:hidden"></th>
        </tr>
      </thead>
      <tbody>
        {medicines.map((m) => {
          const dosagePattern = [
            m.dosage?.morning ? "1" : "0",
            m.dosage?.afternoon ? "1" : "0",
            m.dosage?.evening ? "1" : "0",
            m.dosage?.night ? "1" : "0"
          ].join("-");

          return (
            <tr key={m.id} className="border-b border-slate-100">
              <td className="py-4">
                <div className="flex items-center gap-2">
                  {m.isInjection && <Syringe size={12} className="text-blue-500" />}
                  <p className="text-sm font-black text-slate-800 uppercase">{m.name}</p>
                </div>
                <p className="text-[10px] font-bold text-slate-400 italic mt-1">{m.duration}</p>
              </td>

              <td className="py-4">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-[13px] font-black text-slate-800 tracking-[0.1em] px-2 py-1 bg-slate-50 rounded border border-slate-100 print:bg-transparent print:border-none">
                    {dosagePattern}
                  </span>
                  {m.ml && (
                    <span className="text-[11px] font-black text-blue-600 border border-blue-100 px-2 py-1 rounded bg-blue-50 print:bg-transparent print:border-slate-200 print:text-slate-800">
                      {m.ml} ML
                    </span>
                  )}
                </div>
              </td>

              <td className="py-4 text-right">
                <span className="text-[10px] font-black text-slate-600 uppercase bg-slate-100 px-2 py-1 rounded print:bg-transparent print:border print:border-slate-200">
                  {m.timing}
                </span>
              </td>

              <td className="py-4 text-right print:hidden">
                <button onClick={() => setMedicines(medicines.filter((i) => i.id !== m.id))} className="text-slate-300 hover:text-red-500 p-1">
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>

    {/* Doctor Signature */}
    <div className="mt-20 flex justify-end print:mt-24">
      <div className="text-center w-64 border-t-2 border-slate-900 pt-3 print:border-slate-800">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-900">Dr. {hospitalInfo.doctorName || "Authorized Signatory"}</p>
        <p className="text-[8px] font-bold text-slate-400 uppercase mt-1">Reg No: {hospitalInfo.registrationNumber}</p>
      </div>
    </div>
  </div>
</div>
      </div>
    </div>
  );
};

export default ConsultationPage;